import { Component, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Subscription } from "rxjs";
import { MessageQueueDispatcher } from "@message-queue";
import { IAiProgressEvent } from "@models";

interface IAiActivityStep {
	text: string;
	state: "running" | "done" | "error";
}

/**
 * Live "what the AI is doing" box. Self-subscribes to MessageQueueDispatcher.aiProgress (steps streamed over
 * SignalR) and shows them in order with a spinner on the active step and a check on completed ones. The host
 * just sets [sessionId] (ONE GUID per conversation, reused across follow-up asks) — only events for that session
 * are shown. Only the MOST RECENT query's steps are kept: the list resets when sessionId changes (new conversation)
 * and again at the start of each ask within it — see onEvent for how a new ask is detected.
 */
@Component({
	selector: "ai-activity",
	standalone: true,
	imports: [CommonModule],
	templateUrl: "./ai-activity.component.html",
	styleUrls: ["./ai-activity.component.css"]
})
export class AiActivityComponent implements OnInit, OnChanges, OnDestroy {
	@Input() sessionId: string;

	steps: IAiActivityStep[] = [];

	private sub: Subscription;
	private currentSessionId: string;

	constructor(private messageQueueDispatcher: MessageQueueDispatcher, private zone: NgZone) {
	}

	ngOnInit() {
		this.sub = this.messageQueueDispatcher.aiProgress.subscribe(evt => this.onEvent(evt));
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes["sessionId"]) {
			// New run -> start a fresh list.
			this.currentSessionId = this.sessionId;
			this.steps = [];
		}
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}

	private onEvent(evt: IAiProgressEvent) {
		if (!evt)
			return;

		// Tolerate either camelCase (default) or PascalCase (some SignalR JSON configs) payload keys.
		const e = evt as any;
		const sessionId = e.sessionId ?? e.SessionId;
		const kind = e.kind ?? e.Kind;
		const text = e.text ?? e.Text;
		const index = e.index ?? e.Index; // 0-based position of this event WITHIN its ask (see below)

		if (!this.currentSessionId || sessionId !== this.currentSessionId)
			return;

		// SignalR callbacks can fire outside Angular's zone — run inside it so the view updates.
		this.zone.run(() => {
			// The server builds a fresh progress reporter per ask, so its first event is always index 0 — a reliable
			// "a NEW query just started" marker within this (reused-across-follow-ups) session. Keep only the most
			// recent query's steps: clear the prior ask's lines before this one lands. Left to accumulate, the
			// "Understanding…/Thinking…/Found N…" lines from every follow-up stack up and overflow the fixed-height
			// panel — and they carry no lasting value (no questions or answers, just transient status).
			if (index === 0)
				this.steps = [];

			// A new event means the previous step finished.
			this.steps.forEach(s => { if (s.state === "running") s.state = "done"; });

			if (kind === "complete") {
				if (text)
					this.steps.push({ text, state: "done" });
			}
			else if (kind === "error") {
				this.steps.push({ text: text || "Something went wrong", state: "error" });
			}
			else if (text) {
				this.steps.push({ text, state: "running" });
			}
		});
	}
}
